import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { tokens, Button, Icon, Loader } from '@hotjar/hotsauce';

import type { ContentStatus } from '@hotjar/webapp/domains/highlights/model';

import { useRefreshedRecordingHighlight } from '../use-refreshed-recording-highlight';

const BACKGROUND_COLOR = tokens.color.neutral_800;

const StyledVideo = styled.video`
  background-color: ${BACKGROUND_COLOR};
`;

const NotDoneWrapper = styled.section`
  background-color: ${BACKGROUND_COLOR};
  color: ${tokens.color.text_default_inverse};

  align-content: center;
  display: grid;
  grid-template-rows: auto auto auto;
  grid-row-gap: ${tokens.spacer.x2};
  justify-items: center;
  padding: ${tokens.spacer.x2} 0;
`;

interface RecordingHighlightContentProps {
  className?: string;
  highlight: {
    contentStatus: ContentStatus;
    fullItemUrl: string;
    id: string;
    imageUrl: string;
    metadata: {
      videoUrl: string | null;
    };
    type: 'recording';
  };
  siteId: Parameters<typeof useRefreshedRecordingHighlight>[0];
}

export function RecordingHighlightContent({
  className,
  highlight: initialHighlight,
  siteId,
}: RecordingHighlightContentProps): JSX.Element {
  const { t } = useTranslation();

  const { data: highlight } = useRefreshedRecordingHighlight(siteId, initialHighlight);

  switch (highlight.contentStatus) {
    case 'failed':
      return (
        <NotDoneWrapper className={className}>
          <Icon icon="error" size="l" />
          {t('highlights:single.content-generation.failed')}
          <Button as="a" href={highlight.fullItemUrl} isExternal>
            {t('highlights:single.content-generation.back')}
          </Button>
        </NotDoneWrapper>
      );
    case 'source_deleted':
      return (
        <NotDoneWrapper className={className}>
          <Icon icon="error" size="l" />
          {t('highlights:single.content-generation.source-deleted')}
        </NotDoneWrapper>
      );
    case 'creating':
      return (
        <NotDoneWrapper className={className}>
          <Loader size="l" />
          {t('highlights:single.content-generation.creating')}
        </NotDoneWrapper>
      );
    case 'done':
      return (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <StyledVideo className={className} controls poster={highlight.imageUrl} autoPlay>
          <source type="video/mp4" src={highlight.metadata.videoUrl ?? undefined} />
        </StyledVideo>
      );
    default:
      return (
        <NotDoneWrapper className={className}>
          <Icon icon="error" size="l" />
          {t('highlights:single.content-generation.unknown')}
          <Button as="a" href={highlight.fullItemUrl} isExternal>
            {t('highlights:single.content-generation.see-recording')}
          </Button>
        </NotDoneWrapper>
      );
  }
}
